import React, { useCallback, useState } from "react";
import { observer } from "mobx-react";
import Dropdown from "react-bootstrap/Dropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis, faStar } from "@fortawesome/free-solid-svg-icons";

import { useAPI } from "../../providers/ApiProvider";
import { useAppStore } from "../../providers/AppStoreProvider";
import { toastWithAsyncFetch } from "../../utils/helper";

import { CreateComment } from "./CreateComment";
import { Tooltip } from "../../components/Tooltip";
import { ReactionButton } from "../../components/Reaction/ReactionButton";

const findLinkAndAddTab = (str) => {
  if (str === "") return str;

  const urlRegex =
    /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/g;

  return str.replace(urlRegex, (url) => {
    return `<a target="_blank" href="${url}">${url}</a>`;
  });
};

export const SingleComment = observer(
  ({ comment, isChild, showReply, onShowReply, parentId, isLastChild = false }) => {
    const store = useAppStore();
    const api = useAPI();
    const [isEditing, setIsEditing] = useState(false);

    const handleShowReply = useCallback(() => {
      store.user && onShowReply((showReply) => !showReply);
    }, [onShowReply]);

    const deleteCommentHandler = () => {
      store.commentModal.setEntityId(comment.id);
      store.commentModal.toggle();
    };

    const featureHandler = () => {
      const featured = comment.featured?.status === "active";

      toastWithAsyncFetch(
        {
          loading: `${featured ? "Đang bỏ" : "Đang"} đánh dấu nổi bật...`,
          success: `${featured ? "Bỏ đánh dấu" : "Đánh dấu"} nổi bật thành công`,
          error: `Xảy ra lỗi, ${featured ? "bỏ đánh dấu" : "đánh dấu"} nổi bật thất bại!`,
        },
        () =>
          api.callApi("featureComment", {
            params: { pk: comment.id },
            body: { status: featured ? "inactive" : "active" },
          }),
        (res) => {
          comment.setFeature(res.data.featured);
        },
      );
    };

    const updateHandler = (newComment) => {
      api.callApi("updateComment", { params: { pk: comment.id }, body: newComment }).then((res) => {
        store.updateComment(res.data.comment);
        setIsEditing(false);
      });
    };

    const showListReaction = () => {
      store.reactionModal.setEntityId(comment.id);
      store.reactionModal.toggle();
    };

    const commentImageClickHandler = (commentId) => {
      store.productDetailModal.setEntityId(commentId);
      store.productDetailModal.toggle();
    };

    return (
      <div
        id={`comment-${comment.id}`}
        className="comment position-relative"
        style={{ paddingLeft: isChild ? 72 : 0 }}
      >
        {(!!comment.childComments?.length || showReply) && (
          <div className="tree-view-vertical position-absolute" />
        )}
        {((isChild && !isLastChild) || (showReply && isLastChild)) && (
          <div className="tree-view-vertical position-absolute top-0 h-100" />
        )}
        {isChild && <div className="tree-view-curved position-absolute" />}

        {isEditing ? (
          <div className="mt-3">
            <CreateComment
              parentId={parentId}
              comment={comment}
              trigger
              onCancel={() => setIsEditing(false)}
              onSubmit={updateHandler}
            />
          </div>
        ) : (
          <>
            <div className="d-flex flex-row align-items-center mt-4">
              <div className="me-2 mt-1 mb-auto">
                <div className="avatar avatar-lg overflow-hidden rounded-circle">
                  <img src={comment.creator?.avatar_url} className="w-100" />
                </div>
              </div>

              <div className="d-flex flex-column">
                <div className="content flex-grow-1 d-flex flex-column rounded-20 p-4 py-3">
                  <div className="d-flex flex-column flex-md-row flex-row justify-content-start align-items-start align-items-lg-center">
                    <div className="fw-bold">{comment.creator?.name}</div>
                    {comment.creator?.isTeacher && (
                      <div className="badge bg-danger fw-semibold rounded ms-2">Giảng viên</div>
                    )}
                  </div>
                  {comment.content && (
                    <p
                      className="m-0 mt-1"
                      dangerouslySetInnerHTML={{ __html: findLinkAndAddTab(comment.content) }}
                    />
                  )}

                  {comment.thumbnail_url && (
                    <div className="comment-img position-relative mt-3">
                      {comment.featured?.status === "active" ? (
                        <Tooltip title={`Đánh dấu nổi bật bởi ${comment.featured?.creator.name}`}>
                          <div
                            role="button"
                            className="feature position-absolute shadow-6 rounded-circle top-0 end-0 m-3"
                            data-active={comment.featured?.status === "active"}
                            onClick={featureHandler}
                          >
                            <FontAwesomeIcon icon={faStar} />
                          </div>
                        </Tooltip>
                      ) : (
                        <div
                          role="button"
                          className="feature position-absolute shadow-6 rounded-circle top-0 end-0 m-3"
                          data-active={comment.featured?.status === "active"}
                          onClick={featureHandler}
                        >
                          <FontAwesomeIcon icon={faStar} />
                        </div>
                      )}
                      <img
                        role="button"
                        src={comment.thumbnail_url}
                        className="image w-100 mb-2 rounded-10"
                        onClick={() => commentImageClickHandler(comment.id)}
                      />
                    </div>
                  )}

                  {comment.reacts?.length > 0 && (
                    <div className="reaction-count" onClick={showListReaction}>
                      <div className="reaction-list">
                        {Object.keys(comment.topReaction).map((key) => {
                          return (
                            <div
                              key={key}
                              className="reaction-list-item"
                              style={{
                                backgroundImage: `url(${process.env.PUBLIC_URL}/assets/icons/${key}.svg)`,
                              }}
                            ></div>
                          );
                        })}
                      </div>
                      <div className="reaction-num">{comment.reacts?.length}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-center h-100 ms-2">
                {store.hasPermission("update_comment") || comment.creator.id === store.user?.id ? (
                  <Dropdown>
                    <Dropdown.Toggle className="btn rounded-circle btn-more outline-0">
                      <FontAwesomeIcon icon={faEllipsis} />
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="animate slideIn">
                      <Dropdown.Item
                        className="rounded-10 px-3 py-2"
                        onClick={() => setIsEditing(true)}
                      >
                        Chỉnh sửa
                      </Dropdown.Item>
                      <Dropdown.Item
                        className="rounded-10 px-3 py-2"
                        onClick={deleteCommentHandler}
                      >
                        Xoá
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                ) : (
                  <div style={{ width: "38px", height: "38px" }} />
                )}
              </div>
            </div>

            <div className="comment-actions d-flex flex-row ps-4" style={{ marginLeft: 40 }}>
              <ReactionButton comment={comment} />
              {!isChild && (
                <div role="button" onClick={handleShowReply} className="comment-action py-2 ms-3">
                  Phản hồi
                </div>
              )}
              <div className="text-black-50 py-2 ms-3">{comment.time_elapsed}</div>
            </div>
          </>
        )}
      </div>
    );
  },
);
